import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Tv,
  Settings,
  Download,
  BarChart3,
  Smartphone,
  X,
  CreditCard,
  Wallet,
  ListChecks,
  Clock3,
  Archive,
} from 'lucide-react';
import classNames from 'classnames';

/**
 * Sidebar Navigation Component
 */
const Sidebar = ({ isOpen, setIsOpen }) => {
  const location = useLocation();

  const [subscriptionsOpen, setSubscriptionsOpen] = useState(true);

  const menuItems = [
    {
      label: 'Dashboard',
      icon: LayoutDashboard,
      path: '/',
    },
    {
      label: 'Channels',
      icon: Tv,
      path: '/channels',
    },
    {
      label: 'Devices',
      icon: Smartphone,
      path: '/devices',
    },
    {
      label: 'Payment Methods',
      icon: Wallet,
      path: '/payments',
    },
    {
      label: 'Analytics',
      icon: BarChart3,
      path: '/analytics',
    },
    {
      label: 'Subscriptions',
      icon: CreditCard,
      path: '/subscriptions/packages',
      children: [
        {
          label: 'Packages',
          icon: ListChecks,
          path: '/subscriptions/packages',
        },
        {
          label: 'Pending Requests',
          icon: Clock3,
          path: '/subscriptions/requests/pending',
        },
        {
          label: 'Approved Requests',
          icon: Archive,
          path: '/subscriptions/requests/approved',
        },
        {
          label: 'Rejected Requests',
          icon: X,
          path: '/subscriptions/requests/rejected',
        },
        {
          label: 'History',
          icon: BarChart3,
          path: '/subscriptions/history',
        },
        {
          label: 'Subscription Analytics',
          icon: Settings,
          path: '/subscriptions/analytics',
        },
      ],
    },
    {
      label: 'WebPay Configuration',
      icon: CreditCard,
      path: '/webpay',
    },
    {
      label: 'APK Manager',
      icon: Download,
      path: '/apk',
    },
    {
      label: 'Configuration',
      icon: Settings,
      path: '/config',
    },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden"
          onClick={() => setIsOpen(false)}
        ></div>
      )}

      {/* Sidebar */}
      <div
        className={classNames(
          'fixed md:static w-64 h-screen bg-gray-800 border-r border-gray-700 flex flex-col transition-transform duration-300 z-30',
          !isOpen && '-translate-x-full md:translate-x-0'
        )}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                <Tv className="text-white" size={24} />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">IPTV SaaS</h1>
                <p className="text-xs text-gray-400">Control Panel</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="md:hidden text-gray-400 hover:text-white"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Menu Items */}
        <nav className="flex-1 px-4 py-6 space-y-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);

            if (item.children) {
              return (
                <div key={item.path} className="space-y-1">
                  <button
                    type="button"
                    onClick={() => setSubscriptionsOpen((prev) => !prev)}
                    className={classNames(
                      'flex items-center justify-between w-full px-4 py-3 rounded-lg transition-colors duration-200',
                      active || subscriptionsOpen
                        ? 'bg-purple-600 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    )}
                  >
                    <span className="flex items-center space-x-3">
                      <Icon size={20} />
                      <span className="font-medium">{item.label}</span>
                    </span>
                    <span>{subscriptionsOpen ? '−' : '+'}</span>
                  </button>

                  {subscriptionsOpen && (
                    <div className="space-y-1 pl-8">
                      {item.children.map((child) => {
                        const ChildIcon = child.icon;
                        const childActive = isActive(child.path);
                        return (
                          <Link
                            key={child.path}
                            to={child.path}
                            className={classNames(
                              'flex items-center space-x-3 px-4 py-2 rounded-lg text-sm transition-colors duration-200',
                              childActive
                                ? 'bg-purple-600 text-white'
                                : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                            )}
                          >
                            <ChildIcon size={16} />
                            <span>{child.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <Link
                key={item.path}
                to={item.path}
                className={classNames(
                  'flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors duration-200',
                  active
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                )}
              >
                <Icon size={20} />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700">
          <p className="text-xs text-gray-500 text-center">
            v1.0.0 • Production Ready
          </p>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
